// CSV Import Service - Universal data migration utility for SLIK POS
// Supports importing barbers, clients, and appointments from legacy systems

import { Barber } from '../services/barberDataService';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Client } from '../services/clientDataService';
import Papa from 'papaparse';

export interface ImportResult {
  success: boolean;
  totalRows: number;
  successCount: number;
  failedCount: number;
  errors: Array<{ row: number; error: string; data: any }>;
  warnings: Array<{ row: number; warning: string; data: any }>;
}

export interface BarberCSVRow {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role?: string;
  skills?: string; // Comma-separated
  certifications?: string; // Comma-separated
  experience?: string;
  commissionRate?: string;
  status?: string;
  colorCode?: string;
}

export interface ClientCSVRow {
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  membershipType?: string;
  birthday?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  preferredBarber?: string;
  preferredTimes?: string; // Comma-separated
  allergies?: string; // Comma-separated
  notes?: string;
  totalSpent?: string;
  visits?: string;
  loyaltyPoints?: string;
  tags?: string; // Comma-separated
  referralSource?: string;
  joinDate?: string;
}

export interface AppointmentCSVRow {
  clientName?: string;
  clientPhone?: string;
  clientEmail?: string;
  barberName?: string;
  barberId?: string;
  service: string;
  date: string;
  time: string;
  duration?: string;
  price?: string;
  status?: string;
  notes?: string;
  // Recurring appointment fields
  recurring?: string; // 'true' or 'false'
  recurringPattern?: string; // 'weekly', 'biweekly', 'monthly'
  recurringEndDate?: string;
  recurringDayOfWeek?: string; // For weekly/biweekly
  recurringDayOfMonth?: string; // For monthly
}

export class CSVImportService {

  /**
   * Parse CSV file and return typed data
   */
  static parseCSV<T>(file: File): Promise<{ data: T[]; errors: any[] }> {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header: string) => {
          // Normalize headers: trim, lowercase, remove special chars
          return header.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
        },
        complete: (results) => {
          resolve({
            data: results.data as T[],
            errors: results.errors
          });
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  }

  /**
   * Validate and transform barber CSV row
   */
  static validateBarberRow(row: any, rowIndex: number): { valid: boolean; barber?: Omit<Barber, 'id'>; errors: string[] } {
    const errors: string[] = [];

    // Required fields
    if (!row.firstname && !row.name) {
      errors.push('Missing first name');
    }
    if (!row.lastname && !row.name) {
      errors.push('Missing last name');
    }
    if (!row.email) {
      errors.push('Missing email');
    }
    if (!row.phone) {
      errors.push('Missing phone');
    }

    // Email validation
    if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
      errors.push('Invalid email format');
    }

    if (errors.length > 0) {
      return { valid: false, errors };
    }

    // Handle name field (could be "John Doe" format)
    let firstName = row.firstname || '';
    let lastName = row.lastname || '';

    if (!firstName && row.name) {
      const nameParts = row.name.split(' ');
      firstName = nameParts[0] || '';
      lastName = nameParts.slice(1).join(' ') || '';
    }

    // Transform to Barber format
    const barber: Omit<Barber, 'id'> = {
      firstName,
      lastName,
      email: row.email,
      phone: row.phone,
      role: row.role || 'Barber',
      skills: row.skills ? row.skills.split(',').map((s: string) => s.trim()) : [],
      certifications: row.certifications ? row.certifications.split(',').map((c: string) => c.trim()) : [],
      experience: row.experience ? parseInt(row.experience) : 0,
      commissionRate: row.commissionrate ? parseFloat(row.commissionrate) : 50,
      status: (row.status?.toLowerCase() || 'active') as 'active' | 'inactive' | 'on-leave',
      colorCode: row.colorcode || undefined,
      avgServiceTime: 30,
      avgRating: 4.5,
      monthlyRevenue: 0,
      returningClients: 0,
      totalClients: 0,
      availability: {
        monday: { start: '9:00', end: '18:00', available: true },
        tuesday: { start: '9:00', end: '18:00', available: true },
        wednesday: { start: '9:00', end: '18:00', available: true },
        thursday: { start: '9:00', end: '18:00', available: true },
        friday: { start: '9:00', end: '18:00', available: true },
        saturday: { start: '9:00', end: '17:00', available: true },
        sunday: { start: '', end: '', available: false }
      },
      careerStage: 'Barber',
      joinDate: row.joindate ? new Date(row.joindate) : new Date(),
      languages: ['English']
    };

    return { valid: true, barber, errors: [] };
  }

  /**
   * Validate and transform client CSV row
   */
  static validateClientRow(row: any, rowIndex: number): { valid: boolean; client?: any; errors: string[] } {
    const errors: string[] = [];

    // Debug: Log available columns on first row
    if (rowIndex === 2) {
      console.log('CSV Row columns:', Object.keys(row));
      console.log('Row data:', row);
    }

    // Required fields - check multiple possible column name variations
    const hasFirstName = row.firstname || row.name || row.first || row.fname || row.clientname || row.client;
    const hasLastName = row.lastname || row.name || row.last || row.lname || row.clientname || row.client;
    const hasPhone = row.phone || row.phonenumber || row.mobile || row.cellphone || row.tel || row.telephone || row.mobilenumber;

    if (!hasFirstName) {
      errors.push(`Missing first name (available columns: ${Object.keys(row).join(', ')})`);
    }
    if (!hasLastName) {
      errors.push(`Missing last name (available columns: ${Object.keys(row).join(', ')})`);
    }
    if (!hasPhone) {
      errors.push(`Missing phone (available columns: ${Object.keys(row).join(', ')})`);
    }

    if (errors.length > 0) {
      return { valid: false, errors };
    }

    // Handle name field - try multiple column variations
    let firstName = row.firstname || row.first || row.fname || '';
    let lastName = row.lastname || row.last || row.lname || '';

    // If still no name, try full name field and split it
    if (!firstName && (row.name || row.clientname || row.fullname || row.client)) {
      const fullName = row.name || row.clientname || row.fullname || row.client;
      const nameParts = fullName.trim().split(/\s+/); // Split on any whitespace
      firstName = nameParts[0] || '';
      lastName = nameParts.slice(1).join(' ') || '';
    }

    // Get phone from various possible column names
    const phone = row.phone || row.phonenumber || row.mobile || row.cellphone || row.tel || row.telephone || row.mobilenumber || '';

    // Transform to Client format - map to match clientDataService.Client interface
    const totalVisits = row.visits || row.totalvisits ? parseInt(row.visits || row.totalvisits) : 0;
    const totalSpent = row.totalspent ? parseFloat(row.totalspent) : 0;

    const client = {
      firstName,
      lastName,
      email: row.email || row.emailaddress || '',
      phone,
      membershipType: (row.membershiptype?.toLowerCase() || 'regular') as 'regular' | 'vip' | 'premium' | 'model',
      joinDate: row.joindate ? new Date(row.joindate) : new Date(),
      lastVisit: row.lastvisit ? new Date(row.lastvisit) : undefined,
      totalVisits,
      totalSpent,
      averageTicket: totalVisits > 0 ? totalSpent / totalVisits : 0,
      rebookingRate: row.rebookingrate ? parseFloat(row.rebookingrate) : 0,
      noShowCount: row.noshowcount ? parseInt(row.noshowcount) : 0,
      cancellationCount: row.cancellationcount ? parseInt(row.cancellationcount) : 0,
      notes: row.notes || '',
      tags: row.tags ? row.tags.split(',').map((t: string) => t.trim()) : [],
      status: (row.status?.toLowerCase() || 'active') as 'active' | 'inactive' | 'dormant' | 'blacklisted',
      loyaltyPoints: row.loyaltypoints || row.slikcoins ? parseInt(row.loyaltypoints || row.slikcoins) : 0,
      birthday: row.birthday || '',
      familyMembers: row.familymembers ? row.familymembers.split(',').map((f: string) => f.trim()) : [],
      preferredBarber: row.preferredbarber || '',
      preferences: {
        appointmentReminders: row.appointmentreminders?.toLowerCase() !== 'false',
        marketingEmails: row.marketingemails?.toLowerCase() !== 'false',
        smsNotifications: row.smsnotifications?.toLowerCase() !== 'false',
        preferredTimes: row.preferredtimes ? row.preferredtimes.split(',').map((t: string) => t.trim()) : [],
        allergies: row.allergies ? row.allergies.split(',').map((a: string) => a.trim()) : []
      }
    };

    return { valid: true, client, errors: [] };
  }

  /**
   * Validate and transform appointment CSV row
   */
  static validateAppointmentRow(row: any, rowIndex: number): { valid: boolean; appointment?: any; errors: string[] } {
    const errors: string[] = [];

    // Required fields
    if (!row.service) {
      errors.push('Missing service');
    }
    if (!row.date) {
      errors.push('Missing date');
    }
    if (!row.time) {
      errors.push('Missing time');
    }
    if (!row.clientphone && !row.clientemail && !row.clientname) {
      errors.push('Missing client information (need at least phone, email, or name)');
    }

    if (errors.length > 0) {
      return { valid: false, errors };
    }

    // Parse date and time
    const appointmentDate = new Date(row.date);
    if (isNaN(appointmentDate.getTime())) {
      errors.push('Invalid date format');
      return { valid: false, errors };
    }

    const appointment = {
      clientPhone: row.clientphone || '',
      clientEmail: row.clientemail || '',
      clientName: row.clientname || '',
      barberName: row.barbername || '',
      barberId: row.barberid || '',
      service: row.service,
      date: row.date,
      time: row.time,
      duration: row.duration ? parseInt(row.duration) : 30,
      price: row.price ? parseFloat(row.price) : 0,
      status: (row.status?.toLowerCase() || 'scheduled') as 'scheduled' | 'confirmed' | 'completed' | 'cancelled',
      notes: row.notes || '',
      // Recurring fields
      recurring: row.recurring?.toLowerCase() === 'true',
      recurringPattern: row.recurringpattern || '',
      recurringEndDate: row.recurringenddate || '',
      recurringDayOfWeek: row.recurringdayofweek || '',
      recurringDayOfMonth: row.recurringdayofmonth || ''
    };

    return { valid: true, appointment, errors: [] };
  }

  /**
   * Generate sample CSV templates
   */
  static generateBarberTemplate(): string {
    const headers = [
      'firstName',
      'lastName',
      'email',
      'phone',
      'role',
      'skills',
      'certifications',
      'experience',
      'commissionRate',
      'status',
      'colorCode',
      'joinDate'
    ];

    const sample = [
      'John',
      'Doe',
      'john.doe@salon.com',
      '(555) 123-4567',
      'Master Barber',
      'Fades,Beard Trim,Hair Design',
      'Master Barber,Color Specialist',
      '8',
      '60',
      'active',
      '#4F46E5',
      '2020-01-15'
    ];

    return headers.join(',') + '\n' + sample.join(',');
  }

  static generateClientTemplate(): string {
    const headers = [
      'firstName',
      'lastName',
      'email',
      'phone',
      'membershipType',
      'birthday',
      'address',
      'city',
      'state',
      'zipCode',
      'preferredBarber',
      'preferredTimes',
      'allergies',
      'notes',
      'totalSpent',
      'visits',
      'loyaltyPoints',
      'tags',
      'referralSource',
      'joinDate'
    ];

    const sample = [
      'Jane',
      'Smith',
      'jane.smith@email.com',
      '(555) 987-6543',
      'vip',
      '1985-03-20',
      '123 Main St',
      'Los Angeles',
      'CA',
      '90001',
      'John Doe',
      'mornings,weekends',
      'none',
      'Prefers short cuts',
      '450.00',
      '15',
      '250',
      'vip,loyal',
      'referral',
      '2023-01-10'
    ];

    return headers.join(',') + '\n' + sample.join(',');
  }

  static generateAppointmentTemplate(): string {
    const headers = [
      'clientName',
      'clientPhone',
      'clientEmail',
      'barberName',
      'service',
      'date',
      'time',
      'duration',
      'price',
      'status',
      'notes',
      'recurring',
      'recurringPattern',
      'recurringEndDate'
    ];

    const sample = [
      'Jane Smith',
      '(555) 987-6543',
      'jane.smith@email.com',
      'John Doe',
      'Haircut & Style',
      '2025-01-15',
      '10:00',
      '45',
      '65.00',
      'confirmed',
      'Regular customer',
      'true',
      'monthly',
      '2025-12-31'
    ];

    return headers.join(',') + '\n' + sample.join(',');
  }

  /**
   * Download template as CSV file
   */
  static downloadTemplate(type: 'barber' | 'client' | 'appointment'): void {
    let content = '';
    let filename = '';

    switch (type) {
      case 'barber':
        content = this.generateBarberTemplate();
        filename = 'barber_import_template.csv';
        break;
      case 'client':
        content = this.generateClientTemplate();
        filename = 'client_import_template.csv';
        break;
      case 'appointment':
        content = this.generateAppointmentTemplate();
        filename = 'appointment_import_template.csv';
        break;
    }

    const blob = new Blob([content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}
